import { Avatar, Button, Kbd, Tooltip } from "@heroui/react";
import { Sidebar } from "@heroui-pro/react";
import { LogOut, Search } from "lucide-react";
import { useLocation } from "react-router-dom";

import { NAV_GROUPS } from "./nav";

import { useAuthStore } from "@/stores/authStore";

const isCurrent = (pathname: string, href: string) =>
  pathname === href || pathname.startsWith(`${href}/`);

function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "A"
  );
}

export function SidebarBody({
  onSearch,
  onLogout,
}: {
  onSearch: () => void;
  onLogout: () => void;
}) {
  const { pathname } = useLocation();
  const user = useAuthStore((state) => state.user);
  const name =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    "Dukkanti Admin";

  return (
    <>
      <Sidebar.Header className="gap-3">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="bg-(--foreground) text-(--background) flex size-8 items-center justify-center rounded-lg text-sm font-semibold"
          >
            D
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold">Dukkanti</span>
            <span className="text-muted text-xs">Admin</span>
          </div>
        </div>
        <button
          aria-label="Search or jump to, Command K"
          className="bg-surface text-muted shadow-surface flex h-9 w-full cursor-pointer items-center gap-2 rounded-xl px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-(--focus)"
          type="button"
          onClick={onSearch}
        >
          <Search className="size-4" />
          Search
          <Kbd className="ms-auto">
            <Kbd.Abbr keyValue="command" />
            <Kbd.Content>K</Kbd.Content>
          </Kbd>
        </button>
      </Sidebar.Header>

      <Sidebar.Content>
        {NAV_GROUPS.map((group) => (
          <Sidebar.Group key={group.label}>
            <Sidebar.GroupLabel>{group.label}</Sidebar.GroupLabel>
            <Sidebar.Menu aria-label={group.label}>
              {group.items.map((item) => (
                <Sidebar.MenuItem
                  key={item.href}
                  href={item.href}
                  id={item.href}
                  isCurrent={isCurrent(pathname, item.href)}
                  textValue={item.label}
                >
                  <Sidebar.MenuIcon>
                    <item.icon className="size-4" />
                  </Sidebar.MenuIcon>
                  <Sidebar.MenuLabel>{item.label}</Sidebar.MenuLabel>
                </Sidebar.MenuItem>
              ))}
            </Sidebar.Menu>
          </Sidebar.Group>
        ))}
      </Sidebar.Content>

      <Sidebar.Footer>
        <div className="flex items-center gap-2.5 px-1">
          <Avatar size="sm">
            <Avatar.Fallback>{initials(name)}</Avatar.Fallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="truncate text-sm font-medium">{name}</span>
            <span className="text-muted text-xs">Admin</span>
          </div>
          <Tooltip delay={300}>
            <Button
              isIconOnly
              aria-label="Log Out"
              size="sm"
              variant="ghost"
              onPress={onLogout}
            >
              <LogOut className="size-4" />
            </Button>
            <Tooltip.Content>Log out</Tooltip.Content>
          </Tooltip>
        </div>
      </Sidebar.Footer>
    </>
  );
}
